import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useActualizarCausa, useCausas, useCrearCausa } from '@/hooks/useCausas';
import type { Causa } from '@adelante/shared';
import { CircleNotch, Plus } from '@phosphor-icons/react';
import { useState } from 'react';
import { toast } from 'sonner';

export function CausasPantalla() {
  const causasQ = useCausas();
  const causas = causasQ.data?.data ?? [];
  const [modal, setModal] = useState<{ modo: 'crear' } | { modo: 'editar'; causa: Causa } | null>(
    null,
  );

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Causas</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Motivos de no-cumplimiento (NC) e inactividad que el personal selecciona al registrar
            avances.
          </p>
        </div>
        <Button onClick={() => setModal({ modo: 'crear' })}>
          <Plus className="mr-2 h-4 w-4" weight="bold" aria-hidden="true" />
          Nueva causa
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{causas.length} causas</CardTitle>
        </CardHeader>
        <CardContent>
          {causasQ.isLoading && (
            <div className="flex items-center gap-2 py-8 text-muted-foreground">
              <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
              Cargando…
            </div>
          )}
          {causasQ.error && <p className="py-8 text-destructive">Error: {causasQ.error.message}</p>}
          {!causasQ.isLoading && !causasQ.error && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[60px] text-center">Orden</TableHead>
                  <TableHead className="font-mono text-xs">Código</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead className="text-center">NC</TableHead>
                  <TableHead className="text-center">Inactividad</TableHead>
                  <TableHead className="text-center">Activa</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {causas.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="text-center tabular-nums text-muted-foreground">
                      {c.orden}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{c.codigo}</TableCell>
                    <TableCell className="font-medium">{c.descripcion}</TableCell>
                    <TableCell className="text-center">{c.aplica_nc ? '✓' : '—'}</TableCell>
                    <TableCell className="text-center">
                      {c.aplica_inactividad ? '✓' : '—'}
                    </TableCell>
                    <TableCell className="text-center">
                      {c.activo ? (
                        <Badge className="bg-adelante-primary text-adelante-foreground hover:bg-adelante-primary-dark">
                          Activa
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Inactiva</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-xs"
                        onClick={() => setModal({ modo: 'editar', causa: c })}
                      >
                        Editar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {modal && (
        <ModalCausa
          causa={modal.modo === 'editar' ? modal.causa : null}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

function ModalCausa({ causa, onClose }: { causa: Causa | null; onClose: () => void }) {
  const esEdicion = causa !== null;
  const crear = useCrearCausa();
  const actualizar = useActualizarCausa();
  const guardando = crear.isPending || actualizar.isPending;

  const [codigo, setCodigo] = useState(causa?.codigo ?? '');
  const [descripcion, setDescripcion] = useState(causa?.descripcion ?? '');
  const [aplicaNc, setAplicaNc] = useState(causa?.aplica_nc ?? true);
  const [aplicaInact, setAplicaInact] = useState(causa?.aplica_inactividad ?? false);
  const [orden, setOrden] = useState(String(causa?.orden ?? 0));
  const [activo, setActivo] = useState(causa?.activo ?? true);

  async function handleGuardar() {
    if (codigo.trim().length < 1 || descripcion.trim().length < 2) {
      toast.error('Código y descripción son obligatorios');
      return;
    }
    try {
      if (esEdicion && causa) {
        await actualizar.mutateAsync({
          id: causa.id,
          body: {
            codigo: codigo.trim(),
            descripcion: descripcion.trim(),
            aplica_nc: aplicaNc,
            aplica_inactividad: aplicaInact,
            activo,
            orden: Number(orden) || 0,
          },
        });
        toast.success('Causa actualizada');
      } else {
        await crear.mutateAsync({
          codigo: codigo.trim(),
          descripcion: descripcion.trim(),
          aplica_nc: aplicaNc,
          aplica_inactividad: aplicaInact,
          orden: Number(orden) || 0,
        });
        toast.success('Causa creada');
      }
      onClose();
    } catch (e) {
      toast.error('Error al guardar', {
        description: e instanceof Error ? e.message : 'desconocido',
      });
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && !guardando && onClose()}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>{esEdicion ? 'Editar causa' : 'Nueva causa'}</DialogTitle>
          <DialogDescription>
            Las causas marcadas como NC aparecen al registrar sub-partidas no completadas.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="ca-codigo" className="mb-1.5 block text-xs">
                Código
              </Label>
              <Input
                id="ca-codigo"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
                className="font-mono"
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="ca-desc" className="mb-1.5 block text-xs">
                Descripción
              </Label>
              <Input
                id="ca-desc"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
              />
            </div>
          </div>
          <div className="w-24">
            <Label htmlFor="ca-orden" className="mb-1.5 block text-xs">
              Orden
            </Label>
            <Input
              id="ca-orden"
              type="number"
              min={0}
              value={orden}
              onChange={(e) => setOrden(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2 border-t border-border pt-3">
            <Label
              htmlFor="ca-nc"
              className="flex items-center gap-2 text-sm cursor-pointer font-normal"
            >
              <Checkbox
                id="ca-nc"
                checked={aplicaNc}
                onCheckedChange={(v) => setAplicaNc(v === true)}
              />
              Aplica a no-cumplimiento (NC)
            </Label>
            <Label
              htmlFor="ca-inact"
              className="flex items-center gap-2 text-sm cursor-pointer font-normal"
            >
              <Checkbox
                id="ca-inact"
                checked={aplicaInact}
                onCheckedChange={(v) => setAplicaInact(v === true)}
              />
              Aplica a inactividad de obra
            </Label>
            {esEdicion && (
              <Label
                htmlFor="ca-activo"
                className="flex items-center gap-2 text-sm cursor-pointer font-normal"
              >
                <Checkbox
                  id="ca-activo"
                  checked={activo}
                  onCheckedChange={(v) => setActivo(v === true)}
                />
                Activa
              </Label>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={guardando}>
            Cancelar
          </Button>
          <Button onClick={handleGuardar} disabled={guardando}>
            {guardando ? 'Guardando…' : esEdicion ? 'Guardar' : 'Crear'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
